import clsx from "clsx";
import Link from "next/link";
import { ICON_SIZES, Icon } from "@/components/library/Icon";
import type { IconNameExtended } from "@/components/library/Icon";

export type MarketingBlogCategoryCard = {
  id: string;
  name: string;
  description: string;
  countLabel: string;
  href: string;
  icon: IconNameExtended;
};

export type MarketingBlogCategoriesProps = {
  heading: string;
  readMoreLabel: string;
  items: MarketingBlogCategoryCard[];
  className?: string;
};

export function MarketingBlogCategories({
  heading,
  readMoreLabel,
  items,
  className,
}: Readonly<MarketingBlogCategoriesProps>) {
  return (
    <section
      className={clsx("flex justify-center px-4 pb-16 md:pb-24", className)}
    >
      <div className="w-full max-w-6xl">
        <h2 className="font-monument text-2xl font-medium tracking-tight text-black">
          {heading}
        </h2>

        <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {items.map((item) => (
            <Link
              key={item.id}
              href={item.href}
              className="group flex h-full flex-col rounded-2xl bg-white p-6 shadow-sm transition-shadow hover:shadow-md"
            >
              <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-marketing-voice-card">
                <Icon
                  icon={item.icon}
                  size={ICON_SIZES.LG}
                  className="text-gail-primary"
                />
              </span>

              <h3 className="mt-5 font-monument text-lg font-medium text-black">
                {item.name}
              </h3>
              <p className="mt-2 font-monument text-sm leading-relaxed text-gail-gray">
                {item.description}
              </p>

              <div className="mt-auto flex items-center justify-between pt-6">
                <span className="inline-flex items-center gap-1.5 font-monument text-sm font-medium text-gail-primary">
                  {readMoreLabel}
                  <svg
                    aria-hidden="true"
                    className="size-3.5 shrink-0 transition-transform group-hover:translate-x-0.5"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={2}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M5 12h14" />
                    <path d="m13 6 6 6-6 6" />
                  </svg>
                </span>
                <span className="font-monument text-sm text-gail-gray">
                  {item.countLabel}
                </span>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
